import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowRight, Check, Download } from "lucide-react";
import { MaskTextLoad, FadeUp } from "@/components/Reveal";
import { LEVELS, IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {[true, false].map((v) => (
      <button
        key={String(v)}
        type="button"
        onClick={() => onChange(v)}
        data-testid={`${testid}-${v ? "yes" : "no"}`}
        className={`rounded-full px-6 py-2 font-display text-xs uppercase tracking-[0.12em] font-bold transition-all duration-300 ${
          value === v ? "brand-gradient text-white" : "border border-ink/20 text-ink/60 hover:border-magenta"
        }`}
      >
        {v ? "Yes" : "No"}
      </button>
    ))}
  </div>
);

export default function ConferenceRegister() {
  const qrRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // holds saved registration
  const [f, setF] = useState({
    participant_name: "", age: "", institution: "",
    in_school: true, course_of_study: "", level: "",
    qr_method: "WhatsApp", qr_contact: "",
    phone: "", email: "", food_allergies: "",
    parent_name: "", parent_contact: "",
    join_listen_family: false, volunteer: false, receive_event_updates: true,
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.participant_name.trim() || !f.age || !f.institution.trim() || !f.qr_contact.trim()) {
      toast.error("Please fill name, age, institution and your WhatsApp/email for the QR code.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...f, age: parseInt(f.age, 10) };
      const { data } = await axios.post(`${API}/conference-registrations`, payload);
      setDone(data);
      toast.success("You're registered! Your QR code is ready below.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `ChoicesAndDecisions-Ticket-${done?.participant_name?.replace(/\s+/g, "_") || "registration"}.png`;
    a.click();
  };

  const inputCls =
    "w-full bg-transparent border-b border-ink/20 py-3.5 font-sans text-lg text-ink placeholder-ink/35 focus:outline-none focus:border-magenta transition-colors duration-300";
  const labelCls = "font-display text-xs uppercase tracking-[0.18em] text-ink/45";

  if (done) {
    const qrValue = `CHOICES & DECISIONS — 4th Edition\nName: ${done.participant_name}\nReg#: ${done.id}\nEvent: Sat 3 Oct 2026, 7am–4pm\nVenue: Christian Service Center, East Legon`;
    return (
      <div className="bg-cream min-h-screen pt-36 pb-24 px-5 md:px-10" data-testid="conference-success">
        <div className="max-w-xl mx-auto text-center">
          <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mx-auto h-16 w-16 rounded-full brand-gradient flex items-center justify-center">
            <Check size={30} className="text-white" />
          </motion.div>
          <h1 className="mt-6 font-serif font-light text-4xl md:text-5xl text-ink">You're registered!</h1>
          <p className="mt-3 font-sans text-ink/65">
            Welcome, {done.participant_name.split(" ")[0]}. Here is your entry QR code — please save it and bring it on the day.
          </p>

          <div className="mt-8 inline-flex flex-col items-center rounded-3xl border border-ink/10 bg-white card-shadow p-8" ref={qrRef}>
            <QRCodeCanvas value={qrValue} size={220} fgColor="#1B0812" bgColor="#ffffff" level="M" includeMargin />
            <p className="mt-5 font-display text-xs uppercase tracking-[0.16em] text-ink/50">Choices &amp; Decisions · 4th Edition</p>
            <p className="font-serif text-lg text-ink">Living, Not Surviving</p>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button onClick={downloadQR} data-testid="download-qr" className="inline-flex items-center gap-2 rounded-full brand-gradient px-7 py-3.5 font-display text-xs uppercase tracking-[0.14em] font-bold text-white hover:scale-105 transition-transform">
              <Download size={15} /> Download QR
            </button>
          </div>
          <p className="mt-6 font-sans text-sm text-ink/60 max-w-md mx-auto">
            Please <span className="font-semibold text-ink">download and save your QR code</span> above and present it at the entrance on the day.
            {(() => {
              const em = done.qr_method === "Email" ? done.qr_contact : done.email;
              return em && em.includes("@") ? (
                <> A copy has also been emailed to <span className="text-magenta">{em}</span>.</>
              ) : null;
            })()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen">
      <section className="relative grid lg:grid-cols-[0.9fr_1.1fr] min-h-screen">
        {/* Left visual */}
        <div className="relative hidden lg:flex flex-col justify-center gap-8 bg-[#1B0812] px-12 xl:px-16 py-24 overflow-hidden">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="absolute -top-16 -left-16 h-72 w-72 rounded-full brand-gradient blur-[120px] opacity-25 pointer-events-none" />
          <div className="relative z-10">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-coral mb-4">4th Edition · 2026</p>
            <h2 className="font-serif font-light text-4xl xl:text-5xl leading-[1.02] text-cream">Living,<br/>Not Surviving.</h2>
            <div className="mt-8 relative overflow-hidden rounded-3xl aspect-[4/3] ring-1 ring-white/10">
              <img src={IMAGES.youthGroup} alt="Choices & Decisions" className="h-full w-full object-cover" />
            </div>
            <p className="mt-6 font-serif italic text-xl text-cream/85 max-w-sm">
              Saturday, October 3, 2026 · 7:00 AM – 4:00 PM · Christian Service Center, East Legon.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="px-5 md:px-10 lg:px-16 pt-32 pb-20">
          <div className="max-w-xl mx-auto">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-5">Conference Registration</p>
            <h1 className="font-serif font-light text-5xl md:text-6xl leading-[0.9] text-ink">
              <MaskTextLoad lines={["Reserve", "your place."]} />
            </h1>

            <form onSubmit={submit} className="mt-12 space-y-10" data-testid="conference-form">
              {/* Participant */}
              <div className="space-y-6">
                <p className="font-display text-xs uppercase tracking-[0.2em] text-coral">The Participant</p>
                <div>
                  <label className={labelCls}>Full name *</label>
                  <input data-testid="cf-name" className={inputCls} placeholder="Participant's name" value={f.participant_name} onChange={set("participant_name")} />
                </div>
                <div>
                  <label className={labelCls}>Age *</label>
                  <input data-testid="cf-age" type="number" min="1" className={inputCls} placeholder="Age" value={f.age} onChange={set("age")} />
                </div>
                <div>
                  <label className={labelCls}>School / Institution / Workplace *</label>
                  <input data-testid="cf-institution" className={inputCls} placeholder="e.g. Achimota School / University of Ghana / Company name" value={f.institution} onChange={set("institution")} />
                </div>
              </div>

              {/* Education */}
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-display text-xs uppercase tracking-[0.2em] text-coral">Currently in school?</p>
                  <YesNo value={f.in_school} onChange={(v) => setF((s) => ({ ...s, in_school: v }))} testid="cf-in-school" />
                </div>
                {f.in_school && (
                  <div className="space-y-6">
                    <div>
                      <label className={labelCls}>Course of study</label>
                      <input data-testid="cf-course" className={inputCls} placeholder="e.g. General Science, Business" value={f.course_of_study} onChange={set("course_of_study")} />
                    </div>
                    <div>
                      <label className={labelCls}>Level</label>
                      <select data-testid="cf-level" className={`${inputCls} appearance-none cursor-pointer`} value={f.level} onChange={set("level")}>
                        <option value="">Select level</option>
                        {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* QR delivery */}
              <div className="space-y-6">
                <p className="font-display text-xs uppercase tracking-[0.2em] text-coral">Where should we send your QR code? *</p>
                <div className="flex gap-2" data-testid="cf-qr-method">
                  {["WhatsApp", "Email"].map((m) => (
                    <button key={m} type="button" onClick={() => setF((s) => ({ ...s, qr_method: m }))} data-testid={`cf-qr-method-${m.toLowerCase()}`}
                      className={`rounded-full px-6 py-2 font-display text-xs uppercase tracking-[0.12em] font-bold transition-all duration-300 ${f.qr_method === m ? "brand-gradient text-white" : "border border-ink/20 text-ink/60 hover:border-magenta"}`}>
                      {m}
                    </button>
                  ))}
                </div>
                <div>
                  <label className={labelCls}>{f.qr_method === "WhatsApp" ? "WhatsApp number *" : "Email address *"}</label>
                  <input data-testid="cf-qr-contact" className={inputCls} placeholder={f.qr_method === "WhatsApp" ? "+233 ..." : "you@email.com"} value={f.qr_contact} onChange={set("qr_contact")} />
                </div>
              </div>

              {/* Optional contact */}
              <div className="space-y-6">
                <p className="font-display text-xs uppercase tracking-[0.2em] text-coral">Contact (optional)</p>
                <div>
                  <label className={labelCls}>Phone number</label>
                  <input data-testid="cf-phone" className={inputCls} placeholder="Optional" value={f.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label className={labelCls}>Email address</label>
                  <input data-testid="cf-email" type="email" className={inputCls} placeholder="Optional" value={f.email} onChange={set("email")} />
                </div>
              </div>

              {/* Health & guardian */}
              <div className="space-y-6">
                <p className="font-display text-xs uppercase tracking-[0.2em] text-coral">Wellbeing &amp; Guardian</p>
                <div>
                  <label className={labelCls}>Food allergies</label>
                  <input data-testid="cf-allergies" className={inputCls} placeholder="Any allergies we should know about?" value={f.food_allergies} onChange={set("food_allergies")} />
                </div>
                <div>
                  <label className={labelCls}>Parent / guardian name</label>
                  <input data-testid="cf-parent-name" className={inputCls} placeholder="Parent or guardian" value={f.parent_name} onChange={set("parent_name")} />
                </div>
                <div>
                  <label className={labelCls}>Parent / guardian contact</label>
                  <input data-testid="cf-parent-contact" className={inputCls} placeholder="Phone or email" value={f.parent_contact} onChange={set("parent_contact")} />
                </div>
              </div>

              {/* Preferences */}
              <div className="space-y-6">
                <p className="font-display text-xs uppercase tracking-[0.2em] text-coral">A few more things</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-sans text-ink/80 max-w-xs">Would you like to be part of the Listen family?</span>
                  <YesNo value={f.join_listen_family} onChange={(v) => setF((s) => ({ ...s, join_listen_family: v }))} testid="cf-listen" />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-sans text-ink/80 max-w-xs">Would you like to volunteer at our events?</span>
                  <YesNo value={f.volunteer} onChange={(v) => setF((s) => ({ ...s, volunteer: v }))} testid="cf-volunteer" />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-sans text-ink/80 max-w-xs">Receive messages about other events?</span>
                  <YesNo value={f.receive_event_updates} onChange={(v) => setF((s) => ({ ...s, receive_event_updates: v }))} testid="cf-updates" />
                </div>
              </div>

              <button type="submit" disabled={loading} data-testid="conference-submit"
                className="group inline-flex items-center gap-3 rounded-full brand-gradient px-9 py-4 font-display text-sm uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(232,38,111,0.4)] disabled:opacity-60">
                {loading ? "Registering..." : "Complete Registration"}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
